import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import axios from "axios";
import { TrendingUp, TrendingDown, AlertTriangle, Info, X, ShieldAlert, Bug, Users, Gift, Cpu, Landmark, UserMinus, UsersRound } from "lucide-react";

const SEVERITY_STYLES = {
  critical: "bg-red-950 border-red-800 text-red-100",
  warning: "bg-amber-950 border-amber-800 text-amber-100",
  info: "bg-sky-950 border-sky-800 text-sky-100",
};

const SEVERITY_ACCENT = {
  critical: "text-red-400",
  warning: "text-amber-400",
  info: "text-sky-400",
};

const SEVERITY_BADGE = {
  critical: "bg-red-500/20 text-red-300 border-red-500/30",
  warning: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  info: "bg-sky-500/20 text-sky-300 border-sky-500/30",
};

const EVENT_ICONS = {
  health_inspection: ShieldAlert,
  regional_epidemic: Bug,
  staff_strike: Users,
  philanthropist_donation: Gift,
  tech_breakthrough: Cpu,
  health_reform: Landmark,
  talent_drain: UserMinus,
  population_growth: UsersRound,
};

export default function MarketAlertBanner({ simId }) {
  const { t } = useTranslation();
  const [alert, setAlert] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!simId) return;
    axios.get(`${API}/simulations/${simId}/market-alert`)
      .then((res) => { if (res.data.active) setAlert(res.data); })
      .catch(() => {});
  }, [simId]);

  if (!alert || dismissed) return null;

  const sev = alert.severity || "info";
  const events = alert.events || [];

  return (
    <div className="space-y-2" data-testid="market-alert-banner">
      {/* Market conditions banner */}
      {alert.impacts?.length > 0 && (
        <div className={`rounded-lg border px-4 py-3 ${SEVERITY_STYLES[sev]} animate-in fade-in slide-in-from-top-2 duration-500`}>
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5">
              <AlertTriangle className={`w-5 h-5 ${SEVERITY_ACCENT[sev]}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-heading font-semibold">{t("marketAlert.title")}</span>
                {alert.scenario_name && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${SEVERITY_BADGE[sev]}`}>
                    {alert.scenario_name}
                  </span>
                )}
              </div>
              <p className="text-xs opacity-80 leading-relaxed">{t(`marketAlert.${sev}Desc`)}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {alert.impacts.map((imp) => (
                  <div key={imp.key} className="flex items-center gap-1.5 bg-white/10 rounded-md px-2.5 py-1" data-testid={`alert-impact-${imp.key}`}>
                    {imp.direction === "up" ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    <span className="text-[11px] font-medium">{t(`marketAlert.impact_${imp.key}`)}</span>
                    <span className={`text-[11px] font-mono font-bold ${
                      (imp.key === "demand" && imp.direction === "up") || (imp.key === "costs" && imp.direction === "down") ? "text-emerald-400" : "text-red-400"
                    }`}>{imp.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={() => setDismissed(true)} className="shrink-0 p-1 rounded hover:bg-white/10 transition-colors opacity-60 hover:opacity-100" data-testid="alert-dismiss-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Event cards */}
      {events.map((ev, i) => {
        const evSev = ev.severity || "info";
        const IconComp = EVENT_ICONS[ev.event_type] || AlertTriangle;
        return (
          <div
            key={`${ev.event_type}-${i}`}
            className={`rounded-lg border px-4 py-3 ${SEVERITY_STYLES[evSev]} animate-in fade-in slide-in-from-top-2 duration-500`}
            style={{ animationDelay: `${(i + 1) * 150}ms` }}
            data-testid={`event-card-${ev.event_type}`}
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5">
                <IconComp className={`w-5 h-5 ${SEVERITY_ACCENT[evSev]}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-heading font-semibold">{t(`events.${ev.event_type}.title`)}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${SEVERITY_BADGE[evSev]}`}>
                    {t("events.event")}
                  </span>
                </div>
                <p className="text-xs opacity-80 leading-relaxed">{t(`events.${ev.event_type}.description`)}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
